/** One edit to a text: `removed` characters at `at` were replaced by `inserted` new ones. */
interface TextEdit {
  at: number
  removed: number
  inserted: number
}

/** The edit that turns `before` into `after`, matching the longest unchanged start first. */
function editMatchingStartFirst(before: string, after: string): TextEdit {
  const shortest = Math.min(before.length, after.length)
  let prefix = 0

  while (prefix < shortest && before[prefix] === after[prefix]) prefix++

  let suffix = 0

  while (
    suffix < shortest - prefix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) {
    suffix++
  }

  return {
    at: prefix,
    removed: before.length - prefix - suffix,
    inserted: after.length - prefix - suffix,
  }
}

/** The edit that turns `before` into `after`, matching the longest unchanged end first. */
function editMatchingEndFirst(before: string, after: string): TextEdit {
  const shortest = Math.min(before.length, after.length)
  let suffix = 0

  while (
    suffix < shortest &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) {
    suffix++
  }

  let prefix = 0

  while (prefix < shortest - suffix && before[prefix] === after[prefix]) prefix++

  return {
    at: prefix,
    removed: before.length - prefix - suffix,
    inserted: after.length - prefix - suffix,
  }
}

/** Where `[start, end)` is after the edit, or `null` when the edit touches it. */
function placeAfterEdit(edit: TextEdit, start: number, end: number): [number, number] | null {
  const editEnd = edit.at + edit.removed

  // Entirely before the edit, or right up against it: nothing moves.
  if (end <= edit.at) return [start, end]

  // Entirely after the edit: it moves by what the edit added or took away.
  if (start >= editEnd) {
    const shift = edit.inserted - edit.removed

    return [start + shift, end + shift]
  }

  return null
}

/**
 * Follows the range `[start, end)` of `before` into `after`: its new offsets when the text around it
 * was edited, or `null` when the edit touched the range, or when it cannot be told which of two
 * equally good edits happened (deleting one of three identical words reads as deleting either end
 * one). Unsure counts as lost: a word shown again is a nuisance, a misspelling hidden in error is
 * not. A range that comes out reading differently is lost too.
 */
export function mapRangeThroughEdit(
  before: string,
  after: string,
  start: number,
  end: number
): [number, number] | null {
  if (before === after) return [start, end]

  const fromStart = placeAfterEdit(editMatchingStartFirst(before, after), start, end)
  const fromEnd = placeAfterEdit(editMatchingEndFirst(before, after), start, end)

  if (!fromStart || !fromEnd || fromStart[0] !== fromEnd[0] || fromStart[1] !== fromEnd[1]) {
    return null
  }

  return after.slice(fromStart[0], fromStart[1]) === before.slice(start, end) ? fromStart : null
}
